import * as React from 'react'

import {
  TimesheetEditTimesheetRequestFragment,
  UpdateEditTimesheetRequestDocument,
  UpdateEditTimesheetRequestMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Return } from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'

import Form from './Form'
import ModalFooter from './ModalFooter'

interface Props {
  onCloseModal: () => void
  editTimesheetRequest: TimesheetEditTimesheetRequestFragment // we pass this when we want to update an existing request
  hasBreaksEnabled: boolean
  hasLocationEnabled: boolean
  dayBreaks?: AttendanceBreakConfigurations[]
  refreshTimesheet?: () => Promise<Return<void>>
  employeeId: number
}

const EditRequestModal = ({
  onCloseModal,
  editTimesheetRequest,
  hasBreaksEnabled,
  hasLocationEnabled,
  dayBreaks,
  refreshTimesheet,
  employeeId,
}: Props) => {
  const defaultValues = {
    id: editTimesheetRequest.id,
    clockIn: editTimesheetRequest.clockIn || '',
    clockOut: editTimesheetRequest.clockOut || '',
    locationType: editTimesheetRequest.locationType || null,
    attendanceShiftId: editTimesheetRequest?.id,
    observations: editTimesheetRequest.observations,
    date: editTimesheetRequest.date,
    employeeId: employeeId,
    requestType: editTimesheetRequest.requestType,
    workable: editTimesheetRequest.workable,
    timeSettingsBreakConfigurationId: editTimesheetRequest.timeSettingsBreakConfigurationId,
  }

  const dayBreaksOptions = dayBreaks?.map((dayBreak) => ({
    label: dayBreak.name,
    value: dayBreak.timeSettingsBreakConfigurationId || 0,
  }))

  const [updateEditTimesheetRequest] = useMutation(UpdateEditTimesheetRequestDocument, {
    extractorFn: (data) => data.attendanceMutations.updateAttendanceEditTimesheetRequest,
  })
  const now = moment()

  return (
    <FormProvider
      defaultValues={defaultValues}
      validate={(data) => {
        const errors: FormErrors<UpdateEditTimesheetRequestMutationVariables> = {}
        const [clockInHours, clockInMinutes] = data.clockIn.split(':')
        const [clockOutHours, clockOutMinutes] = data.clockOut.split(':')
        const shiftDate = moment(data.date)
        const clockIn = shiftDate
          .clone()
          .utcOffset(now.utcOffset())
          .hours(Number(clockInHours))
          .minutes(Number(clockInMinutes))
        const clockOut = shiftDate
          .clone()
          .utcOffset(now.utcOffset())
          .hours(Number(clockOutHours))
          .minutes(Number(clockOutMinutes))

        const isOvernightShift = clockOut.isBefore(clockIn)
        const clockInIsFuture = !!data.clockIn && clockIn.isAfter(now)
        const clockOutIsFuture =
          !!data.clockOut &&
          (isOvernightShift ? clockOut.clone().add(1, 'day').isAfter(now) : clockOut.isAfter(now))

        if (clockInIsFuture) {
          errors.clockIn = i18n.t('clock_in.edit_timesheet_requests.modal.errors.clock_in_future')
        }

        if (clockOutIsFuture) {
          errors.clockOut = i18n.t('clock_in.edit_timesheet_requests.modal.errors.clock_in_future')
        }

        return errors
      }}
      onSubmit={async (data, { setErrors }) => {
        const result = await updateEditTimesheetRequest({
          variables: data,
        })

        const handled = handleResult(result, (errors) => {
          setErrors(errors)

          app.setFlash({
            type: 'wrong',
            message: errors?.base || errors?.messages || i18n.t('errors.generic_error'),
          })
        })

        if (handled.success && handled.data.editTimesheetRequest) {
          track(trackingEvents.timeTracking.editShiftRequestSubmit, {
            observations: !!data.observations ? 'filled' : 'empty',
          })
          refreshTimesheet && refreshTimesheet()
          onCloseModal()
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        return handled
      }}
    >
      <Modal
        title={i18n.t('clock_in.edit_timesheet_requests.modal.update_request.title')}
        description={i18n.t('clock_in.edit_timesheet_requests.modal.update_request.description')}
        onClose={onCloseModal}
        footer={<ModalFooter onCloseModal={onCloseModal} />}
      >
        <Form
          editTimesheetRequest={editTimesheetRequest}
          hasBreaksEnabled={hasBreaksEnabled}
          hasLocationEnabled={hasLocationEnabled}
          dayBreaksOptions={dayBreaksOptions}
        />
      </Modal>
    </FormProvider>
  )
}

export default observer(EditRequestModal)
